// Phase 3 — Navigation Architecture: NavigationRegistry
//
// Centralized, single source of truth for all application navigation.
// No navigation menus may be hard-coded outside of this module — UI
// components (SidebarNav, MobileBottomNav, LeftNav, ContextSwitcher, etc.)
// must source their menu items from this registry.
//
// NavigationItem shape (JSDoc — this is a JS codebase, no TypeScript):
//
// @typedef {Object} NavigationItem
// @property {string} id
// @property {string} label
// @property {string} route - may contain `:churchId` / `:circleId`
//   placeholders, resolved by getChurchNavigation()/getCircleNavigation().
// @property {string} [icon] - logical icon key, resolved to a component by
//   the consuming UI layer (see components/navigation/iconMap.js).
// @property {"COMMUNITY"|"CHURCH"|"PERSONAL"|"CIRCLE"} domain
// @property {string[]} [requiredPermissions]
// @property {string[]} [allowedRoles]
// @property {string} [requiredSensitivity] - Phase 4: a SensitivityLevel
//   (see web/src/privacy/sensitivityLevels.js). The viewer's maxSensitivity
//   must meet or exceed this level for the item to be visible.
// @property {"COMMUNITY"|"CHURCH"} [requiredDomain] - Phase 4: Trust Domain
//   this item belongs to. Items with no `requiredDomain` are visible
//   regardless of the active Trust Domain (e.g. Personal-domain items).
// @property {boolean} [mobileVisible] - default true
// @property {boolean} [desktopVisible] - default true
// @property {boolean} [feedEnabled]
// @property {boolean} [dashboardEnabled]
// @property {boolean} [comingSoon] - true if the route exists but has no
//   feature implementation yet (renders a placeholder, never a dead link).
//   Extension field — not part of the Phase 3 prompt's NavigationItem
//   definition, but required to satisfy "no orphan routes" without
//   implementing Church features (Governance Rule #14 ordering).
// @property {string} [badgeKey] - key into a counts map (unread messages,
//   notifications, etc.), resolved by the consuming UI layer.
// @property {NavigationItem[]} [children]
//
// Navigation defines Context, Routes, and Permissions. Widgets define
// Content, Behavior, and Rendering. This registry must never contain
// business logic, data fetching, or rendering decisions beyond the shape
// above.

import { NAV_DOMAINS } from "./navigationDomains";
import { meetsSensitivityRequirement, SENSITIVITY_LEVELS } from "../privacy/sensitivityLevels";

/**
 * Required Navigation Tree — Global (Step 3 / Step 7.5).
 * 9 items: Home, Churches, Circles, Prayer, Events, Messages, Search,
 * Profile, Settings.
 *
 * Mobile uses a separate five-item projection below so the global desktop
 * inventory can stay complete without overloading the bottom navigation.
 */
export const GLOBAL_NAVIGATION = Object.freeze([
  {
    id: "home",
    label: "Home",
    route: "/",
    exact: true,
    icon: "home",
    domain: NAV_DOMAINS.PERSONAL,
    mobileVisible: true,
    desktopVisible: true,
    dashboardEnabled: true,
    feedEnabled: true,
  },
  {
    id: "churches",
    label: "Churches",
    route: "/churches",
    icon: "church",
    domain: NAV_DOMAINS.COMMUNITY,
    mobileVisible: true,
    desktopVisible: true,
  },
  {
    id: "circles",
    label: "Circles",
    route: "/circles",
    icon: "groups",
    domain: NAV_DOMAINS.COMMUNITY,
    mobileVisible: false,
    desktopVisible: true,
  },
  // Phase 9.5 — Dashboard Composition: Community Discovery surface
  // (Featured Churches, Community Activity, Suggested Circles, etc.).
  // Composes existing searchChurches/communityCircles/prayerFeed/churchEvents
  // queries; introduces no new business logic.
  {
    id: "discover",
    label: "Discover",
    route: "/discover",
    icon: "search",
    domain: NAV_DOMAINS.COMMUNITY,
    dashboardEnabled: true,
    // Discover is the unified "Find & Connect" surface (browse circles, find
    // friends and search churches). On mobile it is reached from Circles/More;
    // the stable bottom navigation is projected by getMobileNavigation().
    mobileVisible: true,
    desktopVisible: true,
  },
  {
    id: "prayer",
    label: "Prayer",
    route: "/prayer-center",
    icon: "volunteerActivism",
    domain: NAV_DOMAINS.PERSONAL,
    mobileVisible: true,
    desktopVisible: true,
    feedEnabled: true,
  },
  {
    id: "events",
    label: "Events",
    route: "/events",
    icon: "event",
    domain: NAV_DOMAINS.COMMUNITY,
    mobileVisible: true,
    desktopVisible: true,
  },
  {
    id: "messages",
    label: "Messages",
    route: "/messages",
    icon: "message",
    domain: NAV_DOMAINS.PERSONAL,
    mobileVisible: true,
    desktopVisible: true,
    badgeKey: "unreadMessages",
  },
  {
    id: "profile",
    label: "Profile",
    route: "/profile",
    icon: "personOutline",
    domain: NAV_DOMAINS.PERSONAL,
    mobileVisible: false,
    desktopVisible: true,
  },
  {
    id: "settings",
    label: "Settings",
    route: "/settings",
    icon: "settings",
    domain: NAV_DOMAINS.PERSONAL,
    mobileVisible: false,
    desktopVisible: true,
  },
]);

const MOBILE_SHARE_ACTION = Object.freeze({
  id: "share",
  label: "Share",
  action: "composePrayer",
  icon: "addCircle",
  domain: NAV_DOMAINS.PERSONAL,
  mobileVisible: true,
  desktopVisible: false,
});

export const INBOX_DESTINATION = Object.freeze({
  id: "inbox",
  label: "Inbox",
  route: "/inbox",
  icon: "notifications",
  domain: NAV_DOMAINS.PERSONAL,
  mobileVisible: true,
  desktopVisible: true,
  matchRoutes: ["/inbox", "/notifications", "/messages"],
  badgeKey: "unreadInbox",
});

/**
 * Supplementary Personal-domain navigation — existing app features that are
 * not part of the approved 9-item Global Navigation but must remain
 * reachable (Success Criterion: "Existing functionality preserved").
 * Rendered alongside Global Navigation by SidebarNav, not by
 * MobileBottomNav (mobile relies on Global Navigation + ContextSwitcher).
 */
export const PERSONAL_NAVIGATION = Object.freeze([
  INBOX_DESTINATION,
  {
    id: "answeredPrayer",
    label: "Answered Prayer",
    route: "/?filter=answered",
    matchSearch: "filter=answered",
    icon: "starBorder",
    domain: NAV_DOMAINS.PERSONAL,
    mobileVisible: false,
    desktopVisible: true,
    badgeKey: "answeredCount",
  },
  {
    id: "myLists",
    label: "My Lists",
    route: "/lists",
    icon: "listAlt",
    domain: NAV_DOMAINS.PERSONAL,
    mobileVisible: false,
    desktopVisible: true,
  },
  {
    id: "friends",
    label: "Friends",
    route: "/friends",
    icon: "people",
    domain: NAV_DOMAINS.PERSONAL,
    mobileVisible: false,
    desktopVisible: true,
  },
  {
    id: "notifications",
    label: "Notifications",
    route: "/notifications",
    icon: "notifications",
    domain: NAV_DOMAINS.PERSONAL,
    mobileVisible: false,
    desktopVisible: true,
    badgeKey: "unreadNotifications",
  },
  {
    id: "platformAdmin",
    label: "Platform Admin",
    route: "/admin",
    icon: "dashboardCustomize",
    domain: NAV_DOMAINS.PERSONAL,
    allowedRoles: ["GLOBAL_ADMIN"],
    mobileVisible: false,
    desktopVisible: true,
  },
  {
    id: "domainInfrastructure",
    label: "Domain Infrastructure",
    route: "/admin/domain-infrastructure",
    icon: "dns",
    domain: NAV_DOMAINS.PERSONAL,
    allowedRoles: ["GLOBAL_ADMIN"],
    mobileVisible: false,
    desktopVisible: true,
  },
  {
    id: "foundingMemberCodes",
    label: "Promo Codes",
    route: "/admin/founding-member-codes",
    icon: "redeem",
    domain: NAV_DOMAINS.PERSONAL,
    allowedRoles: ["GLOBAL_ADMIN"],
    mobileVisible: false,
    desktopVisible: true,
  },
  {
    id: "prayerTags",
    label: "Prayer Tags",
    route: "/admin/prayer-tags",
    icon: "label",
    domain: NAV_DOMAINS.PERSONAL,
    allowedRoles: ["GLOBAL_ADMIN"],
    mobileVisible: false,
    desktopVisible: true,
  },
  {
    id: "docs",
    label: "About",
    route: "/docs/requirements",
    icon: "article",
    domain: NAV_DOMAINS.PERSONAL,
    allowedRoles: ["GLOBAL_ADMIN"],
    mobileVisible: false,
    desktopVisible: true,
  },
]);

/**
 * Required Navigation Tree — Church (Step 7.5).
 * Routes use `:churchId` / `:masterCircleId` placeholders resolved by
 * getChurchNavigation(church).
 *
 * Permission shape mirrors Step 8's examples:
 * - Everyone with church access sees: Church Home, Feed, Circles, Ministries,
 *   Events, Give
 * - Staff/Admin roles progressively see People, Care, Households, Visitors,
 *   Volunteers, Communications, Giving, Analytics, and Administration tabs.
 */
export const CHURCH_NAVIGATION = Object.freeze([
  {
    id: "church-overview",
    label: "Church Home",
    route: "/churches/:churchId",
    icon: "dashboardCustomize",
    domain: NAV_DOMAINS.CHURCH,
    audience: "member",
    dashboardEnabled: true,
  },
  {
    id: "church-feed",
    label: "Feed",
    route: "/churches/:churchId/feed",
    icon: "article",
    domain: NAV_DOMAINS.CHURCH,
    audience: "member",
    feedEnabled: true,
  },
  {
    id: "church-master-circle",
    label: "Church Home",
    route: "/churches/:churchId",
    icon: "groupWork",
    domain: NAV_DOMAINS.CHURCH,
    audience: "member",
    desktopVisible: false,
    mobileVisible: false,
  },
  {
    id: "church-people",
    label: "People",
    route: "/churches/:churchId/members",
    icon: "people",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "GLOBAL_ADMIN"],
  },
  {
    id: "church-care",
    label: "Care",
    route: "/churches/:churchId/care",
    icon: "volunteerActivism",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredDomain: "CHURCH",
    requiredPermissions: ["VIEW_CARE"],
    requiredSensitivity: "CARE_TEAM",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
    children: [
      {
        id: "church-care-dashboard",
        label: "Care Dashboard",
        route: "/churches/:churchId/care",
        icon: "dashboardCustomize",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_CARE"],
        requiredSensitivity: "CARE_TEAM",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-care-queue",
        label: "Care Queue",
        route: "/churches/:churchId/care/queue",
        icon: "volunteerActivism",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_CARE"],
        requiredSensitivity: "CARE_TEAM",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-care-teams",
        label: "Care Teams",
        route: "/churches/:churchId/care/teams",
        icon: "groups",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_CARE"],
        requiredSensitivity: "CARE_TEAM",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-care-reports",
        label: "Care Reports",
        route: "/churches/:churchId/care/reports",
        icon: "insights",
        domain: NAV_DOMAINS.CHURCH,
        audience: "admin",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "CARE_TEAM",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "GLOBAL_ADMIN"],
      },
    ],
  },
  {
    id: "church-households",
    label: "Households",
    route: "/churches/:churchId/households",
    icon: "home",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredDomain: "CHURCH",
    requiredPermissions: ["VIEW_HOUSEHOLDS"],
    requiredSensitivity: "MEMBERS_ONLY",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
    children: [
      {
        id: "church-households-dashboard",
        label: "Households Dashboard",
        route: "/churches/:churchId/households",
        icon: "dashboardCustomize",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_HOUSEHOLDS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-households-members",
        label: "Members",
        route: "/churches/:churchId/households/members",
        icon: "people",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_HOUSEHOLDS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-households-relationships",
        label: "Relationships",
        route: "/churches/:churchId/households/relationships",
        icon: "groupWork",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_HOUSEHOLDS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-households-care",
        label: "Care",
        route: "/churches/:churchId/households/care",
        icon: "volunteerActivism",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_HOUSEHOLDS", "VIEW_CARE"],
        requiredSensitivity: "CARE_TEAM",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-households-attendance",
        label: "Attendance",
        route: "/churches/:churchId/households/attendance",
        icon: "eventAvailable",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_HOUSEHOLDS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-households-communications",
        label: "Communications",
        route: "/churches/:churchId/households/communications",
        icon: "mail",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_HOUSEHOLDS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
    ],
  },
  {
    id: "church-visitors",
    label: "Visitors",
    route: "/churches/:churchId/visitors",
    icon: "personAdd",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredDomain: "CHURCH",
    requiredPermissions: ["VIEW_VISITORS"],
    requiredSensitivity: "MEMBERS_ONLY",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "GLOBAL_ADMIN"],
    children: [
      {
        id: "church-visitors-dashboard",
        label: "Visitor Dashboard",
        route: "/churches/:churchId/visitors",
        icon: "dashboardCustomize",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_VISITORS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-visitors-pipeline",
        label: "Visitor Pipeline",
        route: "/churches/:churchId/visitors/pipeline",
        icon: "trendingUp",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_VISITORS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-visitors-membership",
        label: "Membership Journey",
        route: "/churches/:churchId/visitors/membership",
        icon: "groupWork",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_VISITORS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
      {
        id: "church-visitors-reports",
        label: "Visitor Reports",
        route: "/churches/:churchId/visitors/reports",
        icon: "insights",
        domain: NAV_DOMAINS.CHURCH,
        audience: "admin",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "GLOBAL_ADMIN"],
      },
    ],
  },
  {
    id: "church-groups",
    label: "Circles",
    route: "/churches/:churchId/groups",
    icon: "handshake",
    domain: NAV_DOMAINS.CHURCH,
    audience: "member",
  },
  {
    id: "church-ministries",
    label: "Ministries",
    route: "/churches/:churchId/ministries",
    icon: "groups",
    domain: NAV_DOMAINS.CHURCH,
    audience: "member",
    children: [
      {
        id: "church-ministries-dashboard",
        label: "Ministry Dashboard",
        route: "/churches/:churchId/ministries",
        icon: "dashboardCustomize",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_MINISTRIES"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-ministries-directory",
        label: "Ministry Directory",
        route: "/churches/:churchId/ministries/directory",
        icon: "groups",
        domain: NAV_DOMAINS.CHURCH,
        audience: "member",
      },
    ],
  },
  {
    id: "church-volunteers",
    label: "Volunteers",
    route: "/churches/:churchId/volunteers",
    icon: "volunteerActivism",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredDomain: "CHURCH",
    requiredPermissions: ["VIEW_VOLUNTEERS"],
    requiredSensitivity: "MEMBERS_ONLY",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
    children: [
      {
        id: "church-volunteers-dashboard",
        label: "Volunteer Dashboard",
        route: "/churches/:churchId/volunteers",
        icon: "dashboardCustomize",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_VOLUNTEERS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-volunteers-roster",
        label: "Volunteer Roster",
        route: "/churches/:churchId/volunteers/roster",
        icon: "groups",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_VOLUNTEERS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
    ],
  },
  {
    id: "church-events",
    label: "Events",
    route: "/churches/:churchId/events",
    icon: "event",
    domain: NAV_DOMAINS.CHURCH,
    audience: "member",
  },
  {
    id: "church-give",
    label: "Give",
    route: "/churches/:churchId/give",
    icon: "payments",
    domain: NAV_DOMAINS.CHURCH,
    audience: "member",
  },
  {
    id: "church-communications",
    label: "Communications",
    route: "/churches/:churchId/communications",
    icon: "campaign",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredDomain: "CHURCH",
    requiredPermissions: ["VIEW_COMMUNICATIONS"],
    requiredSensitivity: "MEMBERS_ONLY",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
    children: [
      {
        id: "church-communications-dashboard",
        label: "Dashboard",
        route: "/churches/:churchId/communications",
        icon: "dashboardCustomize",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_COMMUNICATIONS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-communications-announcements",
        label: "Announcements",
        route: "/churches/:churchId/communications/announcements",
        icon: "campaign",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_COMMUNICATIONS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-communications-campaigns",
        label: "Campaigns",
        route: "/churches/:churchId/communications/campaigns",
        icon: "send",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_COMMUNICATIONS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
    ],
  },
  {
    id: "church-giving",
    label: "Giving",
    route: "/churches/:churchId/giving",
    icon: "payments",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredDomain: "CHURCH",
    requiredPermissions: ["VIEW_GIVING"],
    requiredSensitivity: "MEMBERS_ONLY",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "GLOBAL_ADMIN"],
    children: [
      {
        id: "church-giving-dashboard",
        label: "Dashboard",
        route: "/churches/:churchId/giving",
        icon: "dashboardCustomize",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_GIVING"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-giving-funds",
        label: "Funds",
        route: "/churches/:churchId/giving/funds",
        icon: "savings",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_GIVING"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-giving-statements",
        label: "Statements",
        route: "/churches/:churchId/giving/statements",
        icon: "receiptLong",
        domain: NAV_DOMAINS.CHURCH,
        audience: "admin",
        requiredDomain: "CHURCH",
        requiredPermissions: ["MANAGE_GIVING"],
        requiredSensitivity: "CONFIDENTIAL",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "GLOBAL_ADMIN"],
      },
    ],
  },
  {
    id: "church-analytics",
    label: "Analytics",
    route: "/churches/:churchId/analytics",
    icon: "insights",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredDomain: "CHURCH",
    requiredPermissions: ["VIEW_ANALYTICS"],
    requiredSensitivity: "MEMBERS_ONLY",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
    children: [
      {
        id: "church-analytics-dashboard",
        label: "Dashboard",
        route: "/churches/:churchId/analytics",
        icon: "dashboardCustomize",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-analytics-church",
        label: "Church",
        route: "/churches/:churchId/analytics/church",
        icon: "church",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-analytics-ministry",
        label: "Ministry",
        route: "/churches/:churchId/analytics/ministry",
        icon: "groups",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-analytics-visitors",
        label: "Visitors",
        route: "/churches/:churchId/analytics/visitors",
        icon: "personAdd",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-analytics-membership",
        label: "Membership",
        route: "/churches/:churchId/analytics/membership",
        icon: "people",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-analytics-volunteers",
        label: "Volunteers",
        route: "/churches/:churchId/analytics/volunteers",
        icon: "volunteerActivism",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-analytics-care",
        label: "Care",
        route: "/churches/:churchId/analytics/care",
        icon: "volunteerActivism",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "CARE_TEAM",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-analytics-households",
        label: "Households",
        route: "/churches/:churchId/analytics/households",
        icon: "home",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
      {
        id: "church-analytics-attendance",
        label: "Attendance",
        route: "/churches/:churchId/analytics/attendance",
        icon: "eventAvailable",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredDomain: "CHURCH",
        requiredPermissions: ["VIEW_ANALYTICS"],
        requiredSensitivity: "MEMBERS_ONLY",
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "MINISTRY_LEADER", "GLOBAL_ADMIN"],
      },
    ],
  },
  {
    id: "church-roles",
    label: "Roles",
    route: "/churches/:churchId/roles",
    icon: "adminPanelSettings",
    domain: NAV_DOMAINS.CHURCH,
    audience: "admin",
    requiredPermissions: ["MANAGE_ROLES"],
    allowedRoles: ["CHURCH_ADMIN", "GLOBAL_ADMIN"],
  },
  {
    id: "church-prayer-intake",
    label: "Prayer Intake",
    route: "/churches/:churchId/prayer-intake",
    icon: "volunteerActivism",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredPermissions: ["MANAGE_INTAKE"],
    requiredSensitivity: "CARE_TEAM",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "GLOBAL_ADMIN"],
  },
  {
    id: "church-contacts",
    label: "Contacts",
    route: "/churches/:churchId/contacts",
    icon: "people",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredSensitivity: "MEMBERS_ONLY",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "CARE_TEAM", "GLOBAL_ADMIN"],
  },
  {
    id: "church-website",
    label: "Website",
    route: "/churches/:churchId/website",
    icon: "article",
    domain: NAV_DOMAINS.CHURCH,
    audience: "staff",
    requiredPermissions: ["PUBLISH_SITE"],
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "GLOBAL_ADMIN"],
    children: [
      {
        id: "church-website-settings",
        label: "Website Settings",
        route: "/churches/:churchId/website",
        icon: "article",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredPermissions: ["PUBLISH_SITE"],
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "GLOBAL_ADMIN"],
      },
      {
        id: "church-website-designer",
        label: "Designer",
        route: "/churches/:churchId/designer",
        icon: "dashboardCustomize",
        domain: NAV_DOMAINS.CHURCH,
        audience: "staff",
        requiredPermissions: ["PUBLISH_SITE"],
        allowedRoles: ["CHURCH_ADMIN", "PASTOR", "GLOBAL_ADMIN"],
      },
    ],
  },
  {
    id: "church-sermons",
    label: "Sermons",
    route: "/churches/:churchId/sermons",
    icon: "article",
    domain: NAV_DOMAINS.CHURCH,
    audience: "member",
  },
  {
    id: "church-sermon-catalog",
    label: "Manage sermons",
    route: "/churches/:churchId/sermons/manage",
    icon: "article",
    domain: NAV_DOMAINS.CHURCH,
    audience: "admin",
    requiredPermissions: ["PUBLISH_SITE"],
    allowedRoles: ["CHURCH_ADMIN", "GLOBAL_ADMIN"],
  },
  {
    id: "church-settings",
    label: "Settings",
    route: "/churches/:churchId/settings",
    icon: "settings",
    domain: NAV_DOMAINS.CHURCH,
    audience: "admin",
    allowedRoles: ["CHURCH_ADMIN", "GLOBAL_ADMIN"],
  },
  {
    id: "church-audit",
    label: "Audit",
    route: "/churches/:churchId/audit",
    icon: "listAlt",
    domain: NAV_DOMAINS.CHURCH,
    audience: "admin",
    requiredPermissions: ["VIEW_AUDIT_LOG"],
    requiredSensitivity: "CONFIDENTIAL",
    allowedRoles: ["CHURCH_ADMIN", "PASTOR", "ELDER", "GLOBAL_ADMIN"],
  },
  {
    id: "church-administration",
    label: "Administration",
    route: "/churches/:churchId/settings",
    icon: "adminPanelSettings",
    domain: NAV_DOMAINS.CHURCH,
    audience: "admin",
    desktopVisible: false,
    mobileVisible: false,
    allowedRoles: ["CHURCH_ADMIN", "GLOBAL_ADMIN"],
  },
]);

/**
 * Required Navigation Tree — Circle (Step 7.5).
 * Routes use `:circleId` placeholders resolved by getCircleNavigation(circle).
 *
 * Members/Events/Resources have no dedicated UI yet (GroupDetails today is a
 * single combined Feed+Members view) so they route to the Feed and are
 * marked comingSoon, matching the precedent set for Church nav items.
 */
export const CIRCLE_NAVIGATION = Object.freeze([
  {
    id: "circle-dashboard",
    label: "Dashboard",
    route: "/circles/:circleId",
    icon: "dashboardCustomize",
    domain: NAV_DOMAINS.CIRCLE,
    dashboardEnabled: true,
  },
  {
    id: "circle-feed",
    label: "Feed",
    route: "/circles/:circleId/feed",
    icon: "article",
    domain: NAV_DOMAINS.CIRCLE,
    feedEnabled: true,
  },
  {
    id: "circle-prayer",
    label: "Prayer",
    route: "/circles/:circleId/feed",
    icon: "volunteerActivism",
    domain: NAV_DOMAINS.CIRCLE,
    comingSoon: true,
  },
  {
    id: "circle-members",
    label: "Members",
    route: "/circles/:circleId/feed",
    icon: "people",
    domain: NAV_DOMAINS.CIRCLE,
    comingSoon: true,
  },
  {
    id: "circle-events",
    label: "Events",
    route: "/circles/:circleId/feed",
    icon: "event",
    domain: NAV_DOMAINS.CIRCLE,
    comingSoon: true,
  },
  {
    id: "circle-resources",
    label: "Resources",
    route: "/circles/:circleId/feed",
    icon: "listAlt",
    domain: NAV_DOMAINS.CIRCLE,
    comingSoon: true,
  },
]);

function resolveRoute(route, params) {
  return Object.entries(params).reduce(
    (acc, [key, value]) => acc.replaceAll(`:${key}`, value ?? ""),
    route
  );
}

function cloneItems(items) {
  return items.map((item) => ({ ...item }));
}

function resolveItemRoutes(item, params) {
  return {
    ...item,
    route: resolveRoute(item.route, params),
    children: item.children?.map((child) => resolveItemRoutes(child, params)),
  };
}

/** Step 7.5: getGlobalNavigation() */
export function getGlobalNavigation() {
  return cloneItems(GLOBAL_NAVIGATION);
}

/** Supplementary Personal-domain items (see PERSONAL_NAVIGATION). */
export function getPersonalNavigation() {
  return cloneItems(PERSONAL_NAVIGATION);
}

/**
 * Step 7.5: getChurchNavigation()
 * @param {{id: string, masterCircleId?: string|null}} [church]
 */
export function getChurchNavigation(church = {}) {
  const params = {
    churchId: church?.id ?? "",
    masterCircleId: church?.masterCircleId ?? "",
  };

  return CHURCH_NAVIGATION.map((item) => resolveItemRoutes(item, params));
}

/**
 * Step 7.5: getCircleNavigation()
 * @param {{id: string}} [circle]
 */
export function getCircleNavigation(circle = {}) {
  const params = { circleId: circle?.id ?? "" };
  return CIRCLE_NAVIGATION.map((item) => ({
    ...item,
    route: resolveRoute(item.route, params),
  }));
}

/**
 * Step 7.5: getNavigationForRole(role)
 * Filters out items whose `allowedRoles` does not include `role`.
 * Items without `allowedRoles` are visible to all roles.
 * Recurses into `children`.
 */
export function getNavigationForRole(role, items) {
  return items
    .filter((item) => !item.allowedRoles || item.allowedRoles.includes(role))
    .map((item) =>
      item.children
        ? { ...item, children: getNavigationForRole(role, item.children) }
        : item
    );
}

/**
 * Step 7.5: getNavigationForPermissions(permissions)
 * Filters out items whose `requiredPermissions` are not all present in
 * `permissions`. Items without `requiredPermissions` are always visible.
 * Recurses into `children`.
 */
export function getNavigationForPermissions(permissions, items) {
  const granted = new Set(permissions || []);
  return items
    .filter(
      (item) =>
        !item.requiredPermissions ||
        item.requiredPermissions.every((p) => granted.has(p))
    )
    .map((item) =>
      item.children
        ? {
            ...item,
            children: getNavigationForPermissions(permissions, item.children),
          }
        : item
    );
}

/**
 * Phase 4: getNavigationForSensitivity(maxSensitivity, items)
 * Filters out items whose `requiredSensitivity` exceeds `maxSensitivity`
 * (see web/src/privacy/sensitivityLevels.js). Items without
 * `requiredSensitivity` are always visible. Recurses into `children`.
 */
export function getNavigationForSensitivity(maxSensitivity, items) {
  const max = maxSensitivity || SENSITIVITY_LEVELS.MEMBERS_ONLY;
  return items
    .filter(
      (item) =>
        !item.requiredSensitivity || meetsSensitivityRequirement(max, item.requiredSensitivity)
    )
    .map((item) =>
      item.children
        ? { ...item, children: getNavigationForSensitivity(maxSensitivity, item.children) }
        : item
    );
}

/**
 * Phase 4: getNavigationForDomain(domain, items)
 * Filters out items whose `requiredDomain` does not match the active Trust
 * Domain. Items without `requiredDomain` are always visible (Domain
 * Isolation — Phase 4 prompt Step 4). Recurses into `children`.
 */
export function getNavigationForDomain(domain, items) {
  return items
    .filter((item) => !item.requiredDomain || item.requiredDomain === domain)
    .map((item) =>
      item.children
        ? { ...item, children: getNavigationForDomain(domain, item.children) }
        : item
    );
}

/**
 * Returns the stable prayer-first mobile navigation. The Share action and Inbox
 * destination are mobile interaction concepts, while their underlying content
 * remains owned by the existing request, notification and message surfaces.
 */
export function getMobileNavigation(items) {
  const byId = new Map(items.map((item) => [item.id, item]));
  return [
    byId.get("home"),
    byId.get("circles"),
    MOBILE_SHARE_ACTION,
    byId.get("prayer"),
    INBOX_DESTINATION,
  ].filter(Boolean);
}

/** Step 7.5: getDesktopNavigation() — items visible on desktop/tablet. */
export function getDesktopNavigation(items) {
  return items.filter((item) => item.desktopVisible !== false);
}

/**
 * Convenience composite: applies role + permission filtering, the order
 * required for "Dynamic Permission Filtering ... by Domain, Role,
 * Permissions, Context".
 *
 * @param {NavigationItem[]} items
 * @param {{role?: string, permissions?: string[], maxSensitivity?: string, domain?: string}} [opts]
 */
export function filterNavigation(items, { role, permissions, maxSensitivity, domain } = {}) {
  let result = items;
  if (role) result = getNavigationForRole(role, result);
  if (permissions) result = getNavigationForPermissions(permissions, result);
  if (maxSensitivity) result = getNavigationForSensitivity(maxSensitivity, result);
  if (domain) result = getNavigationForDomain(domain, result);
  return result;
}
